import { getLocales } from 'assets/locales/Locale';
import { roundToStep } from 'assets/utils/data/Number';
import Claim from 'models/claims/claim/Model.Claim';
import MediaFile from 'models/core/mediaFile/Model.MediaFile';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import Model from 'models/Model';
import Client from '../../claims/client/Model.Client';
import Product from '../../productManagement/product/Model.Product';
import Vehicle from '../../productManagement/vehicle/Model.Vehicle';
import ContractClientHistory from '../contractClientHistory/Model.ContractClientHistory';
import ContractConsent from '../contractConsent/Model.ContractConsent';
import ContractDocumentAggregateTranslation from '../contractDocumentAggregateTranslation/Model.ContractDocumentAggregateTranslation';
import ContractEnhancement from '../contractEnhancement/Model.ContractEnhancement';
import ContractProductDocument from '../contractProductDocuments/ContractProductDocument';
import ContractStatusAuditLog from '../contractStatusAuditLog/Model.ContractStatusAuditLog';
import Statement from '../statement/Model.Statement';
import ContractDocument from './contractDocument/ContractDocument';
import contractExportApi from './export/Api.Contract.Export';
import contractFindApi from './find/Api.Contract.Find';
import contractListWithLegacyApi from './legacy/listWIthLegacy/Api.Contract.ListWithLegacy';
import contractListApi from './list/Api.Contract.List';
import contractListPublicApi from './listPublic/Api.Contract.ListPublic';
import contractListUnrestrictedApi from './listUnrestricted/Api.Contract.ListUnrestricted';
import contractNotUploadedVertaforeStatusListApi from './notUploadedVertaforeStatusList copy/Api.Contract.NotUploadedVertaforeStatusList';
import contractSaveApi from './save/Api.Contract.Save';
import contractSaveUnrestrictedApi from './saveUnrestricted/Api.Contract.SaveUnrestricted';
import contractSendConsentApi from './sendConsent/Api.Contract.SendConsent';
import contractUnknownVertaforeStatusListApi from './unknownVertaforeStatusList/Api.Contract.UnknownVertaforeStatusList';

export default class Contract
  extends Model<Model.IContract>({ icon: 'fas-cloud' })
  implements Model.Instance<Model.IContract>
{
  id: number;
  legacyId: number;
  legacyProductName?: string;
  legacyDealerName?: string;
  appNumber?: string;
  appNumberWithRevision?: string;
  revisionNumber?: number;
  creator?: string;
  statusCode?: Model.Enum.ContractStatusCode;
  documentTemplate?: string;
  effectiveDate: string;
  expiryDate: string;
  createdAtLocal: string;
  createdAtUtc: string;
  modifiedAtUtc: string;
  productId: number;
  product: Product;
  coApplicantFirstName?: string;
  coApplicantLastName?: string;
  dealerId: number;
  dealer?: Dealer;
  clientId: number;
  client: Client;
  vehicleId: number;
  vehicle: Vehicle;
  termInMonths?: number;
  maxKm?: number;
  deductible?: number;
  customInfo?: string;
  customInfoGroup?: string;
  maxVehiclePriceCAD?: number;
  minVehiclePriceCAD?: number;
  tier?: string;
  class?: string;
  warrantyOption?: string;
  pricingDistributions?: Utils.PricingDistribution;
  distributorDistributions?: Utils.PricingDistribution;
  subfees?: Array<Utils.SurchargeInfo>;
  documents?: MediaFile[];
  transferDocuments?: MediaFile[];
  productDocuments?: ContractProductDocument[];
  documentLanguageIsoCode?: Locale.Languages;
  createdBy: User;
  createdById: number;

  contractConsentId?: number;
  contractConsent?: ContractConsent;

  statementId?: number;
  statement?: Statement;

  isContractTransferred?: boolean;
  isTransferInvisible?: boolean;
  clientHistory?: ContractClientHistory[];

  contractAlerts?: Model.IContractAlert[];

  claims?: Claim[];

  paidOnLocalDate?: string;
  isPackage?: boolean;
  isPayed?: boolean;
  docuSignEnvelopeId?: string;
  signFlowEnvelopeId?: string;
  isSignedWithDocuSing?: boolean;
  isSignedWithSignFlow?: boolean;
  isSignedManually?: boolean;
  isSentForSigning?: boolean;
  documentSigningType?: Model.Enum.ContractSigningTypes;
  documentationAggregateId?: number;
  documentationAggregate?: MediaFile;
  signingCertificateOfCompletionId?: number;
  signingCertificateOfCompletion?: MediaFile;
  contractDocumentAggregateTranslations?: ContractDocumentAggregateTranslation[];
  contractDocuments?: ContractDocument[];
  contractEnhancements?: ContractEnhancement[];

  priceCAD: number;
  originalPriceCAD: number;
  discountPriceCAD: number;
  distributorPriceCAD: number;
  surchargesPriceCAD: number;
  enhancementsPriceCAD: number;

  dealerCostProvincialSalesTax: number;
  dealerCostGstHst: number;
  dealerRemittanceProvincialSalesTax: number;
  dealerRemittanceGstHst: number;
  dealerProfitProvincialSalesTax: number;
  dealerProfitGstHst: number;

  salePriceCAD: number;
  postSale?: boolean;

  //#region Vertafore
  vertaforeStatus?: Model.Enum.VertaforeStatuses;
  //#endregion

  claimCount?: number;

  maxCoverageKm?: number; //vehicle.isNew ? maxKm : vehicle.posOdometerReading + maxKm
  readonly coverageKm: number; //vehicle.isNew ? maxKm : vehicle.posOdometerReading + maxKm

  readonly isSigned: boolean; // isSignedManually || isSignedWithDocusign || isSignedWithSignFlow

  readonly salePriceWithTaxCAD: number;
  readonly totalTaxCAD: number;

  readonly paymentAmountCAD: number; //originalPriceCAD - discountAmountCAD + distributorAmountCAD + surchargesAmountCAD
  readonly paymentTaxCAD: number; //calc same as totalTaxCAD but from paymentAmountCAD
  readonly paymentAmountWithTaxCAD: number; //paymentAmountCAD + paymentTaxCAD

  readonly dealerCost: number;
  readonly dealerCostTaxAmt: number;
  readonly dealerCostTotal: number;
  readonly dealerCost75Percent: number;
  readonly dealerProfit: number;
  readonly dealerProfitTaxAmt: number;
  readonly dealerProfitWithTaxCAD: number;

  constructor(data: Partial<Model.IContract> | Utils.FormData<Model.IContract>, language?: Locale.Languages) {
    super(
      {
        dealer: Dealer,
        client: Client,
        vehicle: Vehicle,
        product: Product,
        createdBy: User,
        documents: [MediaFile],
        contractDocuments: [ContractDocument],
        documentationAggregate: MediaFile,
        signingCertificateOfCompletion: MediaFile,
        contractDocumentAggregateTranslations: [ContractDocumentAggregateTranslation],
        productDocuments: [ContractProductDocument],
        contractConsent: ContractConsent,
        contractEnhancements: [ContractEnhancement],
        contractStatusAuditLogs: [ContractStatusAuditLog],
        clientHistory: [ContractClientHistory],
        claims: [Claim],
        statement: Statement,
        transferDocuments: [MediaFile],
      },
      language
    );
    this.update(data);
  }
  get displayInfo(): Model.DisplayInfo {
    const { lang } = getLocales();
    return {
      id: this.id,
      title: `${this?.appNumber}/${this?.client?.displayInfo?.title}/${this?.vehicle?.vin}` || this.id.toString(),
      info: lang[this.statusCode],
      subtitle: this.dealer?.displayInfo.title,
    };
  }

  get taxExemptPrices() {
    if (this.client.taxExempt) {
      return {
        salePriceTotalCAD: this.salePriceWithTaxCAD,
        paymentAmountTotalCAD: this.paymentAmountCAD,
      };
    } else {
      return {
        salePriceTotalCAD: this.salePriceWithTaxCAD,
        paymentAmountTotalCAD: this.paymentAmountWithTaxCAD,
      };
    }
  }
  get paymentPrice() {
    return roundToStep(this?.originalPriceCAD + this?.discountPriceCAD + this?.distributorPriceCAD, 0.01);
  }

  static async find(params: { id: number }) {
    const { payload, ...rest } = await contractFindApi(params);
    return {
      ...rest,
      payload: new Contract(payload),
    };
  }

  static async list(body?: Api.Core.Contract.List.IRequest) {
    const { payload, ...rest } = await contractListApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new Contract(it)),
        count: payload.count,
      },
    };
  }

  static async listUnrestricted(body?: Api.Core.Contract.ListUnrestricted.IRequest) {
    const { payload, ...rest } = await contractListUnrestrictedApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new Contract(it)),
        count: payload.count,
      },
    };
  }

  static async listPublic(body?: Api.Core.Contract.ListPublic.IRequest) {
    const { payload, ...rest } = await contractListPublicApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new Contract(it)),
        count: payload.count,
      },
    };
  }

  static async listWithLegacy(body?: Api.Core.Contract.ListWithLegacy.IRequest) {
    const { payload, ...rest } = await contractListWithLegacyApi(body);
    return {
      ...rest,
      payload: payload && {
        data: payload.data,
        count: payload.count,
      },
    };
  }

  static async export(query: Api.Core.Contract.Export.IRequest) {
    return await contractExportApi(query);
  }

  static async sendConsent(params: Api.Core.Contract.SendConsent.IRequest) {
    return await contractSendConsentApi(params);
  }

  static async getContractsWithUnknownVertaForeStatus() {
    return await contractUnknownVertaforeStatusListApi();
  }

  static async getContractsWithNotUploadedVertaForeStatus() {
    return await contractNotUploadedVertaforeStatusListApi();
  }

  async save() {
    return await contractSaveApi(this);
  }
  async saveUnrestricted() {
    return await contractSaveUnrestrictedApi(this);
  }
}
